import { Box, Grid } from '@mui/material';
import { useMemo } from 'react';
import CardDTO from '@src/adapters/dto/card/CardDTO';
import { CardModificationType } from '@src/constants/CardModificationType';
import CardCategory from './CardCategory';
import { CardModificationDTO } from '@src/adapters/dto/card/CardModificationDTO';

interface Props {
  card: CardDTO;
  isShowChanges: boolean;
}

interface ItemTypes {
  name: string;
  order: number;
}

export enum ItemType {
  Item = 'Item',
  ItemAdded = 'ItemAdded',
  ItemRemoved = 'ItemRemoved',
  ItemUpdated = 'ItemUpdated',
}

export interface CardItem {
  id: string;
  entityId: string;
  createdAt: Date;
  inventory: {
    id: string;
    name: string;
  };
  current: {
    qtyOpen: number;
    qtyOnHand: number;
    isLoan: boolean;
  };
  modificationType?: ItemType;
  previous?: {
    qtyOpen: number;
    qtyOnHand: number;
    isLoan: boolean;
  };
  type: ItemType;
  isAdded?: boolean;
}

export interface CategoryWithItems {
  name: string;
  order: number;
  items: CardItem[];
}

const leftItemCategories: ItemTypes[] = [
  { name: 'Trays & Instrument Sets', order: 1 },
  { name: 'Supplementary Instruments', order: 2 },
];

const centerItemCategories: ItemTypes[] = [
  { name: 'Sutures', order: 1 },
  { name: 'Dressings', order: 2 },
  { name: 'Blades', order: 3 },
  { name: 'Hypodermics', order: 4 },
  { name: 'Fluids', order: 5 },
  { name: 'Infiltration', order: 6 },
  { name: 'Skin Preparation', order: 7 },
  { name: 'Table Extras', order: 8 },
];

const rightItemCategories: ItemTypes[] = [{ name: 'Consumables', order: 1 }];

const groupItems = (
  card: CardDTO,
  types: ItemTypes[],
  isShowChanges: boolean
) => {
  const filteredItems = card.items.filter((i) =>
    types.some((li) => li.name === i.category.name)
  );

  let categoriesWithItems = filteredItems.reduce<CategoryWithItems[]>(
    (acc, item) => {
      let group = acc.find((g) => g.name === item.category.name);

      if (!group) {
        const type = types.find((t) => t.name === item.category.name);
        group = { name: type!.name, order: type!.order, items: [] };
        acc.push(group);
      }

      group.items.push({
        id: item.id,
        entityId: item.entityId,
        createdAt: item.createdAt,
        inventory: { id: item.inventory.id, name: item.inventory.name },
        type: ItemType.Item,
        current: {
          isLoan: item.isLoan,
          qtyOnHand: item.qtyOnHand,
          qtyOpen: item.qtyOpen,
        },
      });

      return acc;
    },
    []
  );

  if (isShowChanges) {
    const addedMigrations = card.modifications.filter(
      (m) => m.type === CardModificationType.ItemAdded
    );

    for (const migration of addedMigrations) {
      if (migration.type === CardModificationType.ItemAdded) {
        const category = categoriesWithItems.find(
          (c) => c.name === migration.new.category.name
        );
        if (category) {
          const item = category.items.find((i) => i.id === migration.entityId);
          if (item) {
            item.type = ItemType.ItemAdded;
            item.isAdded = true;
          }
        }
      }
    }

    const removedMigrations = card.modifications.filter(
      (m) => m.type === CardModificationType.ItemRemoved
    );

    for (const migration of removedMigrations) {
      //if it is a removed item then we need to add a new item into
      //the correct group as this item will not exists
      if (migration.type === CardModificationType.ItemRemoved) {
        let removedGroup = categoriesWithItems.find(
          (g) => g.name === migration.old.category.name
        );

        if (!removedGroup) {
          const type = types.find(
            (t) => t.name === migration.old.category.name
          );
          if (!type) {
            break;
          }
          removedGroup = {
            name: type!.name,
            order: type!.order,
            items: [],
          };
          categoriesWithItems.push(removedGroup);
        }

        removedGroup.items.push({
          id: migration.id!,
          entityId: migration.entityId,
          createdAt: migration.createdAt,
          inventory: {
            id: migration.old.inventory.id,
            name: migration.old.inventory.name,
          },
          type: ItemType.ItemRemoved,
          current: {
            isLoan: migration.old.isLoan,
            qtyOnHand: migration.old.qtyOnHand,
            qtyOpen: migration.old.qtyOpen,
          },
        });
      }
    }

    const updatedModifications = card.modifications.filter(
      (m) => m.type === CardModificationType.ItemUpdated
    );

    const groupedUpdates = new Map<string, CardModificationDTO>();

    for (const mod of updatedModifications) {
      const existing = groupedUpdates.get(mod.entityId);
      if (!existing || mod.createdAt < existing.createdAt) {
        groupedUpdates.set(mod.entityId, mod);
      }
    }

    for (const update of Array.from(groupedUpdates.values())) {
      if (update.type === CardModificationType.ItemUpdated) {
        const category = categoriesWithItems.find(
          (c) => c.name === update.old.category.name
        );
        if (category) {
          const item = category.items.find((i) => i.id === update.entityId);
          if (item && !item.isAdded) {
            item.type = ItemType.ItemUpdated;
            item.previous = {
              isLoan: update.old.isLoan,
              qtyOnHand: update.old.qtyOnHand,
              qtyOpen: update.old.qtyOpen,
            };
          }
        }
      }
    }
  }

  return categoriesWithItems.sort((a, b) => a.order - b.order);
};

function CardTab({ card, isShowChanges }: Props) {
  const leftItems = useMemo(() => {
    return groupItems(card, leftItemCategories, isShowChanges);
  }, [card, isShowChanges]);

  const centerItems = useMemo(() => {
    return groupItems(card, centerItemCategories, isShowChanges);
  }, [card, isShowChanges]);

  const rightItems = useMemo(() => {
    return groupItems(card, rightItemCategories, isShowChanges);
  }, [card, isShowChanges]);

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4.2}>
          {leftItems &&
            leftItems.map((category) => (
              <CardCategory category={category} isShowChanges={isShowChanges} />
            ))}
        </Grid>
        <Grid item xs={12} md={3.6}>
          {centerItems &&
            centerItems.map((category) => (
              <CardCategory category={category} isShowChanges={isShowChanges} />
            ))}
        </Grid>
        <Grid item xs={12} md={4.2}>
          {rightItems &&
            rightItems.map((category) => (
              <CardCategory category={category} isShowChanges={isShowChanges} />
            ))}
        </Grid>
      </Grid>
    </Box>
  );
}

export default CardTab;
